import type { ReactNode } from "react";

/**
 * Wraps an `<input>` or `<textarea>` with a small × button on the
 * right that clears the value. The button only renders when there's
 * something to clear, so empty fields stay visually quiet.
 *
 * The child element needs enough right padding for the button (`pr-8`
 * on inputs, `pr-9` works for textareas). The wrapper itself is
 * `position: relative` so the × can absolute-position over the input.
 *
 * Pure presentation — the caller owns the value and provides
 * `onClear` to reset it.
 */
export function ClearableField({
  value,
  onClear,
  children,
  textarea = false,
  disabled = false,
  label = "Borrar",
}: {
  value: string;
  onClear: () => void;
  children: ReactNode;
  textarea?: boolean;
  disabled?: boolean;
  label?: string;
}) {
  const hasValue = value != null && value.length > 0;
  return (
    <div className="relative">
      {children}
      {hasValue && !disabled ? (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={onClear}
          aria-label={label}
          className={
            textarea
              ? "absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              : "absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          }
        >
          <svg
            viewBox="0 0 20 20"
            className="h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          >
            <path d="M5 5l10 10M15 5L5 15" />
          </svg>
        </button>
      ) : null}
    </div>
  );
}
